import type { CountryCode } from 'libphonenumber-js/max';
import { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js/max';

import type { PhoneNumber } from 'modules/global/typing/common/phone-number';

import { isString } from 'utils/global';

type PhoneNumberOrStringType = PhoneNumber | string | undefined;

const START_BY_00 = /^\+?(00)?/;
const LEADING_0 = /^0/;

const safeGetCallingCode = (countryCode: string): string | undefined => {
  try {
    return getCountryCallingCode(countryCode as CountryCode);
  } catch {
    return undefined;
  }
};

/**
 * Type guard to check if a value is a PhoneNumber object (with prefix and local properties).
 *
 * @param phoneNumber - The phone number value to check
 * @returns True if the value is a valid PhoneNumber object, false otherwise
 */
const isPhoneNumberObject = (phoneNumber: PhoneNumberOrStringType): phoneNumber is PhoneNumber =>
  !isString(phoneNumber) && isString(phoneNumber?.prefix) && isString(phoneNumber.local);

export const internationalFormatPhoneNumber = (phoneNumberString: string) => {
  try {
    const formattedPhoneNumber = parsePhoneNumber(`+${phoneNumberString.replace(START_BY_00, '')}`);

    const prefix = `+${formattedPhoneNumber.countryCallingCode}`;
    const local = formattedPhoneNumber.formatNational().replace(LEADING_0, '');

    return `${prefix} ${local}`;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return undefined;
};

/**
 * Formats a phone number by normalizing the prefix and local parts.
 * Ensures the prefix starts with '+' and removes leading zeros from the local part.
 * Handles both PhoneNumber objects and string formats.
 *
 * @param phoneNumber - The phone number to format (as object, string, or undefined)
 * @returns The formatted phone number in the same type as input, or undefined if invalid
 */
const formatPhoneNumber = (phoneNumber: PhoneNumberOrStringType): PhoneNumberOrStringType => {
  if (isPhoneNumberObject(phoneNumber)) {
    return {
      prefix: `+${phoneNumber.prefix?.replace(START_BY_00, '')}`,
      local: phoneNumber.local.replace(LEADING_0, '')
    };
  }

  if (isString(phoneNumber)) {
    return internationalFormatPhoneNumber(phoneNumber);
  }

  return undefined;
};

/**
 * Validates whether a phone number is valid according to international phone number standards.
 * Formats the phone number before validation using libphonenumber-js.
 *
 * @param phoneNumber - The phone number to validate (as object, string, or undefined)
 * @returns True if the phone number is valid, false otherwise
 */
export const validatePhoneNumber = (phoneNumber: PhoneNumberOrStringType) => {
  try {
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
    const phone = isPhoneNumberObject(formattedPhoneNumber)
      ? `${formattedPhoneNumber?.prefix}${formattedPhoneNumber?.local}`
      : formattedPhoneNumber;

    return isValidPhoneNumber(phone ?? '');
  } catch {
    return false;
  }
};

/**
 * Validates that the phone number prefix (country calling code) has a valid length.
 * A valid prefix should be between 0 and 3 characters long.
 *
 * @param phoneNumber - The phone number to validate (as object, string, or undefined)
 * @returns True if the phone number is an object with a valid prefix length, false otherwise
 */
export const validatePrefix = (phoneNumber: PhoneNumberOrStringType) =>
  isPhoneNumberObject(phoneNumber) && String(phoneNumber.prefix).length < 4;

/**
 * Determines the country code (ISO 3166-1 alpha-2) associated with a phone number.
 * Parses the phone number and extracts the country information from the prefix and local parts.
 *
 * @param phoneNumber - The phone number to analyze (as object, string, or undefined)
 * @returns The ISO country code (e.g., 'US', 'FR') or undefined if parsing fails
 */
export const getCountryByPhoneNumber = (phoneNumber: PhoneNumberOrStringType) => {
  if (isPhoneNumberObject(phoneNumber)) {
    const formatted = formatPhoneNumber(phoneNumber) as PhoneNumber;
    const { prefix, local } = formatted;

    try {
      const parsed = parsePhoneNumber(`${prefix}${local}`);
      return parsed?.country;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing phone number:', error);
    }
  }
  return undefined;
};

export const getSupportedCountryByPhoneNumber = (
  phoneNumber: PhoneNumberOrStringType,
  supportedCountryCodes: string[]
): string | undefined => {
  const country = getCountryByPhoneNumber(phoneNumber);
  if (!country) {
    return undefined;
  }

  if (supportedCountryCodes.includes(country)) {
    return country;
  }

  const callingCode = safeGetCallingCode(country);
  if (!callingCode) {
    return undefined;
  }

  return supportedCountryCodes.find(code => safeGetCallingCode(code) === callingCode);
};

/**
 * Converts a phone number string into a structured PhoneNumber object with separated prefix and local parts.
 * Parses the string using libphonenumber-js and extracts the country calling code and national number.
 *
 * @param phoneNumberString - The phone number string to convert (with or without '+' prefix)
 * @returns A PhoneNumber object with prefix and local properties, or undefined if parsing fails
 */
export const phoneNumberStringToObject = (phoneNumberString: string): PhoneNumber | undefined => {
  if (!phoneNumberString) {
    return undefined;
  }

  try {
    const parsed = parsePhoneNumber(`+${phoneNumberString.replace(START_BY_00, '')}`);

    return {
      prefix: `${parsed.countryCallingCode}`,
      local: parsed.nationalNumber
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing phone number:', error);
    return undefined;
  }
};
